import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subscription, combineLatest } from 'rxjs';
import * as _ from 'lodash';
import { CustomerData } from 'src/app/models/customer.model';
import { KnBrQuote } from 'src/app/models/kn-br-quote.model';
import { KnBrCommonService } from 'src/app/services/kn-br-common.service';
import { KnBrCustomerContextService } from 'src/app/store/kn-br-customer-context/kn-br-customer-context.service';
import { KN_BR_CUSTOMER_CURRENT } from 'src/constants/api.endpoints.constant';
import { I18nModule, TranslationService } from '@spartacus/core';
import { ListNavigationModule } from '@spartacus/storefront';
import { NgSelectModule } from '@ng-select/ng-select';
import { map } from 'rxjs/operators';
import { KnBrRequestQuoteListingService } from '../kn-br-request-quote-listing.service';
import { Router } from '@angular/router';
import { KnBrDateHelper } from 'src/app/shared/kn-br-date.helper';

@Component({
  selector: 'knbr-kn-br-request-quote-listing',
  standalone: true,
  imports: [CommonModule, AsyncPipe, FormsModule, ReactiveFormsModule, NgSelectModule, ListNavigationModule, I18nModule],
  templateUrl: './kn-br-request-quote-listing.component.html',
  styleUrl: './kn-br-request-quote-listing.component.scss',
})
export class KnBrRequestQuoteListingComponent implements OnInit, OnDestroy {
  quoteSearchForm: UntypedFormGroup;
  brandSalesShipToList$: Observable<any> = this.knBrCommonService.loadBrandSalesShipToList$();
  quoteSearchResult$: Observable<KnBrQuote[]> = this.requestQuoteListingService.list$;
  isLoading$: Observable<boolean> = this.requestQuoteListingService.loading$;
  paginate$: Observable<any> = this.requestQuoteListingService.paginate$;
  sorts$: Observable<any> = this.requestQuoteListingService.sorts$;
  customerContextData$: Observable<CustomerData> = this.knBrCustomerContextService.get$;
  selectedCustomer: CustomerData;
  pBrandName: any[] = [];
  currentPage = 0;
  pageSize = 50;
  searchCriteria: any;
  sortType = 'byCreationDate';
  subscription = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private knBrCommonService: KnBrCommonService,
    private requestQuoteListingService: KnBrRequestQuoteListingService,
    private knBrCustomerContextService: KnBrCustomerContextService,
    private translation: TranslationService,
    private router: Router,
    private knBrDateHelper: KnBrDateHelper
  ) {}

  ngOnInit(): void {
    this.initForm();

    this.subscription.add(
      this.customerContextData$.subscribe((customer) => {
        if (customer) {
          this.selectedCustomer = customer;
        }
        const customerNumber = customer?.customerNumber || KN_BR_CUSTOMER_CURRENT;
        this.knBrCommonService.getBrandSalesShipToList(customerNumber);
        this.submitForm();
      })
    );

    this.subscription.add(
      this.requestQuoteListingService.searchCriteria$.subscribe((criteria) => {
        this.searchCriteria = _.cloneDeep(criteria);
      })
    );

    this.subscription.add(
      this.paginate$.subscribe((paginate) => {
        if (paginate) {
          this.sortType = paginate.sort;
        }
      })
    );

    this.subscription.add(
      this.isLoading$.subscribe((isLoading) => {
        this.knBrCommonService.setSpinner(isLoading);
      })
    );
  }

  ngOnDestroy(): void {
    this.knBrCommonService.setSpinner(false);
    this.subscription.unsubscribe();
  }

  initForm(): void {
    this.quoteSearchForm = this.fb.group({
      quoteRequestNumber: null,
      customerPO: null,
      brandName: null,
      fromDate: null,
      toDate: null,
      quoteNumber: null,
      currentPage: this.currentPage,
      pageSize: this.pageSize,
    });
  }

  submitForm(): void {
    this.currentPage = 0;
    this.search(this.currentPage);
  }

  resetForm(): void {
    this.currentPage = 0;
    this.pBrandName = [];
    this.requestQuoteListingService.reset();
    this.quoteSearchForm.reset({
      quoteRequestNumber: null,
      customerPO: null,
      brandName: null,
      fromDate: null,
      toDate: null,
      quoteNumber: null,
      currentPage: this.currentPage,
      pageSize: this.pageSize,
    });
    this.submitForm();
  }

  loadMore(currentPage: number): void {
    if (this.searchCriteria) {
      this.searchCriteria.currentPage = currentPage + 1;
      this.searchCriteria.sort = this.sortType;
      this.requestQuoteListingService.search(this.searchCriteria);
    }
  }

  changeSortCode(sortCode: string): void {
    if (this.searchCriteria) {
      this.searchCriteria.currentPage = 0;
      this.searchCriteria.sort = sortCode;
    }
    this.sortType = sortCode;
    this.requestQuoteListingService.search(this.searchCriteria);
  }

  getSortLabels(): Observable<{ byCreationDate: string; byRFQNumber: string; byDate: string; byStatus: string; byExpirationDate: string }> {
    return combineLatest([
      this.translation.translate('sorting.creationDate'),
      this.translation.translate('sorting.quoteRequestNumber'),
      this.translation.translate('sorting.validToDate'),
      this.translation.translate('sorting.status'),
      this.translation.translate('sorting.expirationDate'),
    ]).pipe(
      map(([textCreationDate, textQuoteNumber, textByDate, textByStatus, textExpirationDate]) => ({
        byCreationDate: textCreationDate,
        byRFQNumber: textQuoteNumber,
        byDate: textExpirationDate,
        byStatus: textByStatus,
        byExpirationDate: textByDate,
      }))
    );
  }

  getQuoteValue(quote: KnBrQuote): string {
    return quote.referenceQuoteId || quote.code || '';
  }

  getSelectedCustomerSummary(): string {
    if (!this.selectedCustomer) {
      return '';
    }

    const values = [
      this.selectedCustomer.customerNumber,
      this.selectedCustomer.customerName,
      this.selectedCustomer.city,
    ].filter((value) => !!value);

    return values.join(' | ');
  }

  goToRequestDetails(quoteNumber: string): void {
    if (!quoteNumber) {
      return;
    }
    this.router.navigateByUrl(`/quoteRequestDetails/${quoteNumber}`);
  }

  private search(currentPage: number): void {
    const formValue = this.quoteSearchForm.getRawValue();
    const selectedBrands = Array.isArray(this.pBrandName) && this.pBrandName.length ? this.pBrandName : formValue.brandName;
    const brandName = Array.isArray(selectedBrands)
      ? selectedBrands
          .map((brand) => brand?.value || brand?.name || brand)
          .filter((value) => !!value)
          .join(',')
      : selectedBrands || '';

    const payload: any = {
      quoteReferenceCode: formValue.quoteNumber,
      quoteNumber: formValue.quoteRequestNumber,
      purchaseOrderNumber: formValue.customerPO,
      brandName,
      startDate: this.knBrDateHelper.combineDateFormatyyyyMMdd(formValue.fromDate),
      endDate: this.knBrDateHelper.combineDateFormatyyyyMMdd(formValue.toDate),
      currentPage,
      pageSize: formValue.pageSize || this.pageSize,
      sort: this.sortType || 'byCreationDate',
    };

    this.searchCriteria = _.cloneDeep(payload);
    this.requestQuoteListingService.search(payload);
  }
}
